import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { formatUpdated, formatFullUpdated } from '../lib/format';
import { Warning, TrendDown, X, CaretDown, CaretUp } from '@phosphor-icons/react';

export default function RateAlertBanner() {
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/rate-alerts?dismissed=false&limit=100');
      setAlerts(res.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const dismissOne = async (alertId) => {
    try {
      await api.post(`/rate-alerts/${alertId}/dismiss`);
      setAlerts((a) => a.filter((x) => x.alert_id !== alertId));
    } catch { /* ignore */ }
  };

  const increases = alerts.filter((a) => (a.direction || 'increase') === 'increase');
  const decreases = alerts.filter((a) => a.direction === 'decrease');

  if (alerts.length === 0) return null;

  return (
    <div className="sticky top-0 z-30">
      {increases.length > 0 && (
        <AlertBar direction="increase" alerts={increases} onDismiss={dismissOne} />
      )}
      {decreases.length > 0 && (
        <AlertBar direction="decrease" alerts={decreases} onDismiss={dismissOne} />
      )}
    </div>
  );
}

function AlertBar({ direction, alerts, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  const [dismissingAll, setDismissingAll] = useState(false);
  const isIncrease = direction === 'increase';
  const top = alerts[0];

  const dismissAll = async () => {
    if (!window.confirm(`Dismiss all ${alerts.length} rate-${isIncrease ? 'increase' : 'drop'} alerts?`)) return;
    setDismissingAll(true);
    try {
      await Promise.all(alerts.map((a) => api.post(`/rate-alerts/${a.alert_id}/dismiss`).catch(() => null)));
      alerts.forEach((a) => onDismiss(a.alert_id));
    } finally {
      setDismissingAll(false);
    }
  };

  // Styling tokens per direction
  const s = isIncrease
    ? {
        wrap: 'border-b border-red-500/40 bg-red-50',
        icon: <Warning size={16} weight="fill" className="text-red-600 shrink-0 animate-pulse" />,
        labelColor: 'text-red-600',
        labelCount: 'text-red-700',
        body: 'text-red-900',
        dim: 'text-red-700/70',
        accent: 'text-red-700',
        chipBorder: 'border-red-500/30',
        divider: 'border-red-500/30',
        subText: 'text-red-700/70',
        label: `Rate increase${alerts.length > 1 ? 's' : ''} detected`,
        testId: 'rate-alert-banner-increase',
      }
    : {
        wrap: 'border-b border-emerald-500/40 bg-emerald-50',
        icon: <TrendDown size={16} weight="fill" className="text-emerald-600 shrink-0" />,
        labelColor: 'text-emerald-600',
        labelCount: 'text-emerald-700',
        body: 'text-emerald-900',
        dim: 'text-emerald-700/70',
        accent: 'text-emerald-700',
        chipBorder: 'border-emerald-500/30',
        divider: 'border-emerald-500/30',
        subText: 'text-emerald-700/70',
        label: `Permanent rate drop${alerts.length > 1 ? 's' : ''}`,
        testId: 'rate-alert-banner-decrease',
      };

  return (
    <div className={`backdrop-blur-xl ${s.wrap}`} data-testid={s.testId}>
      <div className="px-5 lg:px-8 py-2.5">
        <div className="flex items-center gap-3">
          {s.icon}
          <div className="flex-1 min-w-0">
            <div className={`text-xs label-overline ${s.labelColor} flex items-center gap-2`}>
              <span>{s.label}</span>
              <span className={`font-data ${s.labelCount} text-[11px]`}>{alerts.length}</span>
            </div>
            <div className={`text-sm ${s.body} truncate mt-0.5`}>
              <span className="font-medium">{top.lender_name}</span>
              <span className={s.dim}> · </span>
              <span>{top.product_category} {top.term}{top.ltv_bucket ? ` · ${top.ltv_bucket}` : ''}</span>
              <span className={s.dim}> · </span>
              <span className="font-data">
                {top.old_rate.toFixed(2)}% → <span className={`${s.accent} font-medium`}>{top.new_rate.toFixed(2)}%</span>
              </span>
              <span className={`font-data ${s.labelCount} ml-2`}>
                {top.change_bps > 0 ? `+${top.change_bps}` : top.change_bps}bps
              </span>
              {top.effective_date && (
                <span className={`${s.dim} ml-2 text-xs`}>
                  eff. {new Date(top.effective_date).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>
          </div>
          {alerts.length > 1 && (
            <button
              onClick={() => setExpanded((e) => !e)}
              data-testid={`${s.testId}-expand`}
              className={`text-xs label-overline ${s.labelCount} hover:${s.body} flex items-center gap-1 shrink-0`}
            >
              {expanded ? <>Hide <CaretUp size={11} /></> : <>See all {alerts.length} <CaretDown size={11} /></>}
            </button>
          )}
          <button
            onClick={() => onDismiss(top.alert_id)}
            data-testid={`${s.testId}-dismiss`}
            className={`${s.labelCount} hover:${s.body} shrink-0`}
            title="Dismiss"
          >
            <X size={14} />
          </button>
          {alerts.length > 1 && (
            <button
              onClick={dismissAll}
              disabled={dismissingAll}
              data-testid={`${s.testId}-dismiss-all`}
              className={`text-[10px] label-overline ${s.subText} hover:${s.body} underline-offset-2 hover:underline shrink-0`}
            >
              {dismissingAll ? 'dismissing…' : 'dismiss all'}
            </button>
          )}
        </div>

        {expanded && alerts.length > 1 && (
          <div className={`mt-3 border-t ${s.divider} pt-3 max-h-64 overflow-y-auto space-y-2`}>
            {alerts.slice(1).map((a) => (
              <div key={a.alert_id} className="flex items-center gap-3 text-sm" data-testid={`rate-alert-${a.alert_id}`}>
                <span className={`font-medium ${s.body} shrink-0 min-w-[8rem] truncate`}>{a.lender_name}</span>
                <span className={`${s.dim} text-xs flex-1 truncate`}>
                  {a.product_category} · {a.term}{a.ltv_bucket ? ' · ' + a.ltv_bucket : ''}
                </span>
                <span className={`font-data text-xs ${s.accent} shrink-0`}>
                  {a.old_rate.toFixed(2)} → {a.new_rate.toFixed(2)}
                </span>
                <span className={`font-data text-xs ${s.labelCount} shrink-0`}>
                  {a.change_bps > 0 ? `+${a.change_bps}` : a.change_bps}bps
                </span>
                <span className={`text-[10px] ${s.subText} shrink-0 min-w-[5rem] text-right`} title={formatFullUpdated(a.created_at)}>
                  {formatUpdated(a.created_at)}
                </span>
                <button
                  onClick={() => onDismiss(a.alert_id)}
                  className={`${s.subText} hover:${s.body} shrink-0`}
                  data-testid={`rate-alert-dismiss-${a.alert_id}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
